import { Avatar, Link } from "@heroui/react";

import { useTranslations } from "@/src/i18n/i18n";
import { initials } from "@/src/libs/initials";
import { ROUTES } from "@/src/routes/paths";

/**
 * De quoi nommer et montrer un compte.
 *
 * Structurel à dessein : les identités de la modération, les auteurs de
 * messages, les comptes d'une réaction et les abonnés d'une fiche ont quatre
 * formes différentes dans le contrat, et toutes se rangent ici. `id` est
 * **toujours** l'identifiant de compte — c'est lui qui ouvre la fiche.
 */
export type AccountFace = {
  id: number;
  username: string;
  avatarUrl?: string | null;
};

type AccountBadgeProps = {
  /** `null` = compte supprimé, ou verdict pas encore rendu. */
  account: AccountFace | null;
  /** Ouvre la fiche du compte. Faux pour une simple mention. */
  isLinked?: boolean;
};

/**
 * Un compte, montré comme on le reconnaît : sa photo et son pseudo.
 *
 * Le serveur tranche l'identité pour toute l'API — pseudo et avatar viennent du
 * profil `default`, avec repli sur `accounts.username` pour le nom. **Rien à
 * recalculer ici** : une règle recopiée dans deux clients finit par diverger.
 *
 * ⚠️ `alt=""` sur l'avatar, et ce n'est pas un oubli : le pseudo est juste à
 * côté, en texte. Une alternative qui le répéterait ferait annoncer le compte
 * deux fois de suite par un lecteur d'écran. L'image est décorative parce que
 * l'information est déjà là.
 */
export function AccountBadge({ account, isLinked = false }: AccountBadgeProps) {
  const t = useTranslations("global");

  // Le compte a disparu entre le signalement et sa lecture. Il reste une
  // ligne à afficher : effacer la mention laisserait croire que personne n'a
  // signalé.
  if (account === null) {
    return <span className="text-muted italic">{t("account-deleted")}</span>;
  }

  const face = (
    <>
      <Avatar className="size-5 shrink-0" size="sm">
        {account.avatarUrl ? (
          <Avatar.Image alt="" src={account.avatarUrl} />
        ) : null}
        <Avatar.Fallback className="text-[10px]">
          {initials(account.username)}
        </Avatar.Fallback>
      </Avatar>

      <span className="truncate">{account.username}</span>
    </>
  );

  if (!isLinked) {
    return (
      <span className="inline-flex min-w-0 items-center gap-1.5">{face}</span>
    );
  }

  /*
    La fiche s'ouvre par identifiant de **compte** — c'est ce que le lot A a
    débloqué. Depuis la file de signalements, le signaleur mène donc à son
    dossier en un clic, sans conversion d'identifiant.
  */
  /*
    ⚠️ **La palette met `--link` à `--foreground`**, pas à l'accent : un pseudo
    cliquable est donc, au repos, indiscernable d'un pseudo écrit. C'est voulu
    pour un lien dans une phrase ; ici il faut que la pastille se donne à
    cliquer. D'où l'état de survol — fond et soulignement — plutôt qu'une
    couleur d'accent qui jurerait avec le reste de la palette.
  */
  return (
    <Link
      className="inline-flex min-w-0 items-center gap-1.5 rounded-full py-0.5 pr-2 text-xs hover:bg-surface-hover hover:underline"
      href={ROUTES.account(account.id)}
    >
      {face}
    </Link>
  );
}
